export const STATUSES = ["NOT STARTED", "IN PROGRESS", "DONE", "ON HOLD", "DEPRIORITIZED", "REVIEW"];

export const STATUS_COLORS = {
  "NOT STARTED": "#D8B4E2",
  "IN PROGRESS": "#90C2E7",
  DONE: "#A8E6CF",
  "ON HOLD": "#FFD3B6",
  DEPRIORITIZED: "#FF8B94",
  REVIEW: "#FFC3A0",
};

export const PRIORITIES = ["LOW", "MEDIUM", "URGENT"];

export const PRIORITY_COLORS = {
  LOW: "#A8E6CF",
  MEDIUM: "#FFD3B6",
  URGENT: "#FF8B94",
};

export const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

// Deterministic per-person avatar colour, derived from the name so the same
// person always gets the same hue across the app (tables, board, PTO, team).
const AVATAR_PALETTE = ["#00E0A4", "#90C2E7", "#D8B4E2", "#FFD3B6", "#FF8B94", "#A8E6CF", "#FFC3A0", "#B5A8E6", "#7FD1C1", "#F5B971", "#8FB8FF", "#E6A8C9"];
export function avatarColor(name) {
  const s = (name || "?").trim();
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const hex = AVATAR_PALETTE[h % AVATAR_PALETTE.length];
  return { hex, bg: `${hex}26`, ring: `${hex}66` };
}

export function nameInitials(name) {
  const parts = (name || "?").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Minutes to add to UTC to get the user's local time (send to the backend so
// "Today / This Week / This Month" and overdue anchor to the user's local day).
export const tzOffset = () => -new Date().getTimezoneOffset();

// The user's local calendar date as YYYY-MM-DD (not UTC).
export const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export function formatApiError(err) {
  if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
    return "The request timed out. Please check your connection and try again.";
  }
  const detail = err?.response?.data?.detail;
  if (detail == null && !err?.response) {
    return "Couldn't reach the server. Please check your connection and try again.";
  }
  if (detail == null) return err?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  }
  if (typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function isOverdue(task) {
  if (!task.due_date || task.status === "DONE") return false;
  return task.due_date < localToday();
}

export function timeAgo(iso) {
  const seconds = (Date.now() - new Date(iso).getTime()) / 1000;
  if (seconds < 60) return "just now";
  const minutes = seconds / 60;
  if (minutes < 60) return `${Math.floor(minutes)}m ago`;
  const hours = minutes / 60;
  if (hours < 24) return `${Math.floor(hours)}h ago`;
  const days = hours / 24;
  if (days < 7) return `${Math.floor(days)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export const INACTIVE_DAYS = 14;

// Must match ONLINE_WINDOW in backend/core.py.
export const ONLINE_MINUTES = 3;

// `presence` ("online" | "away" | "offline") comes from the server when available.
export function lastSeen(iso, presence) {
  if (!iso) return { label: "Not seen yet", online: false, away: false, inactive: false };
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  const days = diff / 86400000;
  let label;
  const away = presence === "away";
  const online = presence ? presence === "online" : min < ONLINE_MINUTES;
  if (online) label = "Active now";
  else if (away) label = "Away";
  else if (min < 60) label = `${min} min ago`;
  else if (min < 1440) label = `${Math.floor(min / 60)} h ago`;
  else if (days < 7) label = `${Math.floor(days)}d ago`;
  else label = new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  return { label, online, away, inactive: days >= INACTIVE_DAYS };
}

export const fmtStamp = (iso) =>
  iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "never";

// How long a task has waited in review. Calendar days; same-day shows minutes or hours.
// Shared by the To review page and the Report so both always match.
export function reviewAge(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const now = new Date();
  const days = Math.max(0, Math.round(
    (new Date(now.getFullYear(), now.getMonth(), now.getDate()) - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000));
  if (days > 0) return { days, value: days, unit: days === 1 ? "day" : "days", short: `${days}d` };
  const mins = Math.max(0, Math.floor((now - d) / 60000));
  if (mins < 60) return { days: 0, value: mins, unit: "min", short: `${mins}m` };
  const hrs = Math.floor(mins / 60);
  return { days: 0, value: hrs, unit: hrs === 1 ? "hr" : "hrs", short: `${hrs}h` };
}
